import { Color, Spacing } from '@persona-id/design-tokens';
import { CaretDownIcon } from '@persona-id/design-tokens/icons';
import { useMemo } from 'react';
import styled, { css } from 'styled-components';

import BusinessLookupAddresses from 'dashboard/components/ReportResult/BusinessLookupAddresses';
import BusinessLookupIndustryClassifications from 'dashboard/components/ReportResult/BusinessLookupIndustryClassifications';
import BusinessLookupPeople from 'dashboard/components/ReportResult/BusinessLookupPeople';
import BusinessLookupRegistrations from 'dashboard/components/ReportResult/BusinessLookupRegistrations';
import BusinessLookupSummary from 'dashboard/components/ReportResult/BusinessLookupSummary';
import { MatchIconAlert, MatchIconPositive } from 'dashboard/components/ReportResult/MatchIcons';
import ReportResultLayout from 'dashboard/components/ReportResult/ReportResultLayout';
import TableLayout, {
  EmbeddedTableNullState,
  TableIconTD,
  TableTD,
  TableTH,
  TableTR,
} from 'dashboard/layouts/TableLayout';
import { MatchStatus, ReportBusinessLookup } from 'dashboard/models/report/business-lookup';
import Card from 'design-system/components/Card';
import Sticker, { LabelBackgrounds } from 'design-system/components/Sticker';
import { BASE } from 'design-system/variables/spacing';
import { TransitionDuration } from 'design-system/variables/transitions';
import useToggle from 'lib/hooks/useToggle';
import { FontWeight } from 'lib/interfaces/ThemeSerialized';

export enum PropertyName {
  BusinessName = 'Business name',
  Address = 'Address',
  StreetAddress = 'Street address',
  City = 'City',
  Subdivision = 'Subdivision',
  PostalCode = 'Postal code',
  Ein = 'EIN',
  Website = 'Website',
  PhoneNumber = 'Phone number',
  AssociatedPerson = 'Associated person',
}

const ADDRESS_COMPONENT_PROPERTY_NAMES = [
  PropertyName.StreetAddress,
  PropertyName.City,
  PropertyName.Subdivision,
  PropertyName.PostalCode,
];

interface BusinessLookupProps {
  report: ReportBusinessLookup;
  isRedacted: boolean;
  multiCardFormat: boolean;
}

const BusinessLookup = ({ report, isRedacted, multiCardFormat = false }: BusinessLookupProps) => {
  const {
    query: query,
    'match-status-business-name': matchStatusBusinessName,
    'match-status-address': matchStatusAddress,
    'match-status-address-street': matchStatusAddressStreet,
    'match-status-address-city': matchStatusAddressCity,
    'match-status-address-subdivision': matchStatusAddressSubdivision,
    'match-status-address-postal-code': matchStatusAddressPostalCode,
    'match-status-phone-number': matchStatusPhoneNumber,
    'match-status-associated-person': matchStatusAssociatedPerson,
    'match-status-ein': matchStatusEin,
    'match-status-website': matchStatusWebsite,
  } = report.attributes;

  const {
    'business-name': businessName,
    'address-street-1': addressStreet1,
    'address-city': addressCity,
    'address-subdivision': addressSubdivision,
    'address-postal-code': addressPostalCode,
    'phone-number': phoneNumber,
    'associated-people': associatedPeople,
    website,
    ein,
  } = query || {};

  const matchRows = [
    {
      property: PropertyName.BusinessName,
      submitted: businessName,
      matchStatus: matchStatusBusinessName,
    },
    {
      property: PropertyName.Address,
      submitted: `${addressStreet1}, ${addressCity}, ${addressSubdivision}, ${addressPostalCode}`,
      matchStatus: matchStatusAddress,
    },
    {
      property: PropertyName.StreetAddress,
      submitted: addressStreet1,
      matchStatus: matchStatusAddressStreet,
    },
    {
      property: PropertyName.City,
      submitted: addressCity,
      matchStatus: matchStatusAddressCity,
    },
    {
      property: PropertyName.Subdivision,
      submitted: addressSubdivision,
      matchStatus: matchStatusAddressSubdivision,
    },
    {
      property: PropertyName.PostalCode,
      submitted: addressPostalCode,
      matchStatus: matchStatusAddressPostalCode,
    },
    {
      property: PropertyName.Ein,
      submitted: ein,
      matchStatus: matchStatusEin,
    },
    {
      property: PropertyName.Website,
      submitted: website,
      matchStatus: matchStatusWebsite,
    },
    {
      property: PropertyName.PhoneNumber,
      submitted: phoneNumber,
      matchStatus: matchStatusPhoneNumber,
    },
    {
      property: PropertyName.AssociatedPerson,
      submitted:
        associatedPeople && associatedPeople.length > 0 ? associatedPeople[0]['name-full'] : null,
      matchStatus: matchStatusAssociatedPerson,
    },
  ].filter((row) => row.submitted) as BusinessLookupMatchRow[];

  return isRedacted ? (
    <ReportResultLayout multiCardFormat={multiCardFormat}>
      <div>
        <Sticker>Redacted</Sticker>
      </div>
    </ReportResultLayout>
  ) : (
    <ReportResultLayout multiCardFormat={multiCardFormat}>
      <BusinessLookupMatchTable
        title='Business Match'
        rows={matchRows}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        reportMatchStatusStrength={report.attributes['match-status-strength']}
      />
      <BusinessLookupSummary report={report} />
      <BusinessLookupIndustryClassifications report={report} />
      <BusinessLookupPeople report={report} />
      <BusinessLookupAddresses report={report} />
      <BusinessLookupRegistrations report={report} />
    </ReportResultLayout>
  );
};

const StyledTableTH = styled(TableTH)`
  width: ${(props) => props.width ?? 'auto'};
  padding-top: 4px;
  padding-bottom: 4px;
  font-weight: ${FontWeight.Medium};
`;

const StyledTableTD = styled(TableTD)`
  padding-top: 4px;
  padding-bottom: 4px;
`;

const PaddedSticker = styled(Sticker)`
  margin-left: ${Spacing[4]};
`;

const StyledCaretDownIcon = styled(CaretDownIcon)<{
  expanded: boolean;
  hidden: boolean;
}>`
  transition: transform ${TransitionDuration.Fast};
  transform: rotate(${({ expanded }) => (expanded ? '0' : '-90deg')});

  width: 20px;
  height: 20px;

  ${({ hidden }) =>
    hidden &&
    css`
      opacity: 0;
    `}
`;

const ClickableStyledTableTD = styled(StyledTableTD)`
  cursor: pointer;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

interface BusinessLookupMatchRow {
  property: PropertyName;
  submitted: string;
  matchStatus: MatchStatus;
}
interface BusinessLookupMatchTableProps {
  rows: BusinessLookupMatchRow[];
  title: string;
  reportMatchStatusStrength: MatchStatus | null;
  matchLevelLabel?: string;
}

const BusinessLookupMatchTable = ({
  rows,
  title,
  reportMatchStatusStrength,
  matchLevelLabel = 'Match level',
}: BusinessLookupMatchTableProps) => {
  const [variant, label]: [LabelBackgrounds, string] = useMemo(() => {
    if (reportMatchStatusStrength === MatchStatus.Match) {
      return ['green', 'Full match'];
    } else if (reportMatchStatusStrength === MatchStatus.PartialMatch) {
      return ['green', 'Partial match'];
    } else if (reportMatchStatusStrength === MatchStatus.NoMatch) {
      return ['red', 'No match'];
    } else {
      return ['grey', 'N/A'];
    }
  }, [reportMatchStatusStrength]);

  return (
    <>
      <Card.Title>
        {title}
        {reportMatchStatusStrength != null && (
          <PaddedSticker data-test='match-status__status-sticker' variant={variant}>
            {label}
          </PaddedSticker>
        )}
      </Card.Title>
      <TableLayout
        dataTest='match-status-table'
        showHeader={true}
        isEmpty={rows.length === 0}
        nullState={
          <EmbeddedTableNullState marginVertical={BASE * 2} dataTestBlock='match-status-table'>
            <MatchIconAlert label='No matches found.' />
          </EmbeddedTableNullState>
        }
        headerTR={
          <>
            <StyledTableTH>{/* Empty header for icon column */}</StyledTableTH>
            <StyledTableTH width='35%'>Property</StyledTableTH>
            <StyledTableTH width='35%'>Submitted</StyledTableTH>
            <StyledTableTH>{matchLevelLabel}</StyledTableTH>
          </>
        }
      >
        <BusinessLookupMatchRows rows={rows} />
      </TableLayout>
    </>
  );
};

interface BusinessLookupMatchRowsProps {
  rows: BusinessLookupMatchRow[];
  matchLabelFull?: string;
  matchLabelPartial?: string;
  matchLabelNone?: string;
}

const BusinessLookupMatchRows = ({
  rows,
  matchLabelFull = 'Full',
  matchLabelPartial = 'Partial',
  matchLabelNone = 'None',
}: BusinessLookupMatchRowsProps) => {
  const [expanded, { toggle: toggleExpanded }] = useToggle(false);
  const addressMatchRow = useMemo(
    () => rows.find((row) => row.property === PropertyName.Address),
    [rows],
  );
  const nonAddressComponentRows = useMemo(
    () => rows.filter((row) => !ADDRESS_COMPONENT_PROPERTY_NAMES.includes(row.property)),
    [rows],
  );

  const addressComponentRows = useMemo(
    () => rows.filter((row) => ADDRESS_COMPONENT_PROPERTY_NAMES.includes(row.property)),
    [rows],
  );

  const renderedAddressComponentRows = useMemo(
    () =>
      addressComponentRows.map((row) => {
        let labelValue = 'N/A';
        if (row.submitted) {
          if (row.matchStatus === MatchStatus.Match) {
            labelValue = matchLabelFull;
          } else if (row.matchStatus === MatchStatus.PartialMatch) {
            labelValue = matchLabelPartial;
          } else {
            labelValue = matchLabelNone;
          }
        }
        const rowPassed = [matchLabelFull, matchLabelPartial].includes(labelValue);

        return (
          <TableTR
            key={row.property.split(' ').join('-')}
            data-test={`business-lookup-match-table__${row.property.split(' ').join('-')}`}
          >
            <TableIconTD>{rowPassed ? <MatchIconPositive /> : <MatchIconAlert />}</TableIconTD>
            <StyledTableTD>{row.property}</StyledTableTD>
            <StyledTableTD>{row.submitted ?? '-'}</StyledTableTD>
            <StyledTableTD>{labelValue}</StyledTableTD>
          </TableTR>
        );
      }),
    [addressComponentRows],
  );

  if (addressMatchRow != null && addressMatchRow.matchStatus != null) {
    return (
      <>
        {nonAddressComponentRows.flatMap((row) => {
          let labelValue = 'N/A';
          if (row.submitted) {
            if (row.matchStatus === MatchStatus.Match) {
              labelValue = matchLabelFull;
            } else if (row.matchStatus === MatchStatus.PartialMatch) {
              labelValue = matchLabelPartial;
            } else {
              labelValue = matchLabelNone;
            }
          }
          const rowPassed = [matchLabelFull, matchLabelPartial].includes(labelValue);

          if (row.property === PropertyName.Address) {
            return (
              <>
                <TableTR key={row.property.split(' ').join('-')}>
                  <TableIconTD>
                    {rowPassed ? <MatchIconPositive /> : <MatchIconAlert />}
                  </TableIconTD>
                  <ClickableStyledTableTD
                    onClick={toggleExpanded}
                    id='business-lookup-match-table__clickable-address-row'
                    data-test='business-lookup-match-table__clickable-address-row'
                  >
                    <ButtonContainer>
                      {row.property}
                      <StyledCaretDownIcon
                        expanded={expanded}
                        hidden={false}
                        color={Color.TextPrimary}
                      />
                    </ButtonContainer>
                  </ClickableStyledTableTD>
                  <StyledTableTD>{row.submitted ?? '-'}</StyledTableTD>
                  <StyledTableTD>{labelValue}</StyledTableTD>
                </TableTR>
                {expanded && renderedAddressComponentRows}
              </>
            );
          } else {
            return (
              <TableTR key={row.property.split(' ').join('-')}>
                <TableIconTD>{rowPassed ? <MatchIconPositive /> : <MatchIconAlert />}</TableIconTD>
                <StyledTableTD>{row.property}</StyledTableTD>
                <StyledTableTD>{row.submitted ?? '-'}</StyledTableTD>
                <StyledTableTD>{labelValue}</StyledTableTD>
              </TableTR>
            );
          }
        })}
      </>
    );
  } else {
    return (
      <>
        {rows
          .filter((row) => row.property !== PropertyName.Address)
          .map((row) => {
            let labelValue = 'N/A';
            if (row.submitted) {
              if (row.matchStatus === MatchStatus.Match) {
                labelValue = matchLabelFull;
              } else if (row.matchStatus === MatchStatus.PartialMatch) {
                labelValue = matchLabelPartial;
              } else {
                labelValue = matchLabelNone;
              }
            }
            const rowPassed = [matchLabelFull, matchLabelPartial].includes(labelValue);

            return (
              <TableTR
                key={row.property.split(' ').join('-')}
                data-test={row.property.split(' ').join('-')}
              >
                <TableIconTD>{rowPassed ? <MatchIconPositive /> : <MatchIconAlert />}</TableIconTD>
                <StyledTableTD>{row.property}</StyledTableTD>
                <StyledTableTD>{row.submitted ?? '-'}</StyledTableTD>
                <StyledTableTD>{labelValue}</StyledTableTD>
              </TableTR>
            );
          })}
      </>
    );
  }
};
export default BusinessLookup;
